/**
 * **조직 범위 목록**을 고르는 검색형 멀티셀렉트 — 전사 공지의 `targetOrganizationIds`를 채운다.
 *
 * 사용자 결정(2026-10-19): **검색형 멀티셀렉트.** prod 조직이 **84**이라 전체 목록 `<select>`는
 * 쓰기 어렵고, 회사 행은 그보다 많다(수검업체 등 테넌트가 아닌 회사도 같은 표에 산다).
 *
 * ## ⚠️ 선례가 반쪽이라 둘을 합쳤다
 *
 * | 축 | 선례 | 이 컴포넌트가 가져온 것 |
 * |---|---|---|
 * | 검색형 **단일** 선택 | `pages/report-composer/CatalogCombobox.tsx` | input + 드롭다운 **자체 구현** · 외부 클릭 닫기 · Esc · `role="listbox"`/`option` |
 * | 칩 렌더 | `pages/report-composer/SimulationChip.tsx` | 칩 모양(작은 테두리 + ✕) |
 * | 서버 검색 + 디바운스 | `components/DeleteUserDialog.tsx` | 300ms 디바운스 + react-query |
 *
 * **칩 누적형(멀티)은 레포에 0건**이라 그 셋을 합치는 것이 선례에 맞다 — 새 라이브러리를 들이지
 * 않는다(`CatalogCombobox` 헤더가 「외부 라이브러리 없이 … 기존 admin/Tailwind 패턴」이라 적는다).
 *
 * ## 후보는 `/admin/companies`에서 온다 — `/admin/subscriptions`가 아니다
 *
 * 둘 다 「조직」을 돌려주지만 **집합이 다르다**:
 *
 * - `/admin/subscriptions`는 **`category=WORKSPACE`를 제외한다**(`admin.service.ts`의
 *   `getOrganizations`). 개인 작업공간도 구성원이 있는 **실테넌트**이므로, 그 목록을 후보로 쓰면
 *   후보가 **겨눌 수 있는 집합보다 좁아진다** — 특히 `EXCLUDE`에서 개인 작업공간을 **뺄 길이
 *   없어진다**. 그 결함은 조용하다(목록에 없으니 운영자는 그런 조직이 없다고 믿는다).
 * - `/admin/companies`는 **상위집합**이고 `q`로 **서버 검색**을 지원하며
 *   `@RequirePermission`이 **없다**(`SuperAdminGuard`만) — 즉 권한 축이 이 화면(`system-settings`)과
 *   어긋나지 않는다. `lib/audience-options.ts`가 역할 목록에서 겪은 403 문제가 여기는 없다.
 *
 * ⚠️ **상위집합이라 반대 방향의 느슨함이 있다** — 테넌트가 아닌 회사(수검업체 등)도 후보에 뜨고,
 * 그것을 `INCLUDE`에 담으면 **구성원이 0명이라 아무에게도 보이지 않는 공지**가 된다. 서버
 * `assertTargetsExist`는 **행의 실재만** 보므로(그 JSDoc이 파기된 조직을 같은 이유로 통과시킨다)
 * 이 축을 막지 않는다. ⇒ 화면이 `hasSubscription`을 **배지로** 보여 그 신호를 운영자에게 준다.
 * **막지는 않는다** — 「구성원 없는 조직」을 겨누지 못할 근거가 없고(나중에 생길 수 있다) 그
 * 판정은 공지 저작의 몫이 아니다(그 서버 JSDoc과 같은 자세).
 *
 * ## ⚠️ 동명 회사가 있다 — `category`·대표자·사업자번호를 함께 보인다
 *
 * `AdminCompanyListItem.category` 주석이 「동명 회사 구분용 — 개인 워크스페이스는 등록번호·대표자가
 * 비어 이 셋이 실질 구분자」라 적는다. 이름만 보이면 운영자가 **다른 회사를 겨눈다**.
 *
 * ⚠️ `category`는 **한국어 라벨**로 보인다(2026-10-21 수정 — 그 전까지 `WORKPLACE` 날것이었다).
 * 어휘와 그 사고의 경위는 아래 `describe()` docblock이 소유한다.
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { ORGANIZATION_CATEGORY_SHORT_LABELS } from '@safetyhub/shared';
import { adminCompaniesApi, type AdminCompanyListItem } from '../lib/api';

interface Props {
  /** 고른 조직 id — 뜻은 부모의 `orgScope`가 정한다(이 컴포넌트는 모드를 모른다) */
  value: string[];
  onChange: (ids: string[]) => void;
  disabled?: boolean;
  /** 상한 — 서버 DTO의 `MAX_ORG_TARGETS`와 같은 값을 부모가 준다 */
  max: number;
}

/**
 * 폼 컨트롤 공통 클래스.
 *
 * ⚠️ 반지름은 **역할 토큰**이다(`local/no-untokenized-radius`의 안내표: 「폼 컨트롤·버튼·세그먼티드
 * 트랙은 `rounded-control`(10px)」). bare `rounded`(4px)는 그 규칙이 체크박스·라디오 갈래로 비워
 * 둔 값이라 여기 쓰면 토큰 밖 값이 조용히 굳는다.
 */
const FIELD_CLS =
  'w-full text-sm border border-gray-300 rounded-control px-2 py-1.5 focus:outline-none focus:ring-1 focus:ring-brand-400';

/**
 * 동명 구분용 보조 문구 — 없는 칸은 건너뛴다(빈 `·`가 줄줄이 남지 않게)
 *
 * ⚠️ `category`는 **한국어 라벨로 옮긴다** — 날것은 운영자에게 개발 enum을 보인다. 2026-10-21까지
 * 이 칸이 `WORKPLACE · 홍길동 · 123-45-67890`처럼 떴고, 하필 **`WORKPLACE`(사업장)와
 * `WORKSPACE`(개인/팀)가 한 글자 차이**라 사람이 가르는 데 가장 약한 형태였다. 이 문구의 목적이
 * 바로 「동명 회사를 구별해 다른 회사를 겨누지 않게」인데(위 docblock) 그 목적을 스스로 깨고 있었다.
 *
 * 라벨 소유자는 shared `ORGANIZATION_CATEGORY_SHORT_LABELS` 하나다 — admin의 다른 네 자리
 * (`VerificationDetailModal` ×2 · `VerificationQueue` · report-composer `EditorPage`)가 전부 그것을
 * 쓴다. 여기서 사본을 만들면 같은 규약의 두 번째 구현이 된다.
 *
 * ⚠️ `?? item.category`는 **타입이 아니라 런타임을 위한 폴백**이다 — `AdminCompanyListItem.category`는
 * `OrganizationCategory`로 정확히 타이핑돼 있지만, 서버가 enum 값을 늘리고 web 번들이 아직 옛
 * 것이면 맵에 없는 값이 올 수 있다. 그때 `undefined`를 보이지 않고 날것이라도 보인다(선례
 * `audienceLabel`의 「모르는 참조를 숨기면 비어 있다고 믿는다」와 같은 자세).
 */
function describe(item: AdminCompanyListItem): string {
  const category = ORGANIZATION_CATEGORY_SHORT_LABELS[item.category] ?? item.category;
  return [category, item.ownerName, item.registrationNumber].filter(Boolean).join(' · ');
}

/**
 * 적재 병합 — **값이 달라진 게 없으면 `prev`를 그대로 돌려준다.**
 *
 * ⚠️ 새 객체를 돌려주면 `unresolvedIds` memo가 다시 돌고, 그 새 배열이 해소 effect를 다시 깨워
 * **렌더 루프**가 된다(그 고리는 실재한다: memo → effect → setState → memo). 「변화 없으면 같은
 * 참조」가 그 고리를 끊는 유일한 자리이므로 지우지 말 것.
 */
function mergeNames(
  prev: Record<string, string>,
  pairs: Iterable<readonly [string, string]>,
): Record<string, string> {
  let changed = false;
  const next = { ...prev };
  for (const [id, name] of pairs) {
    if (next[id] !== name) {
      next[id] = name;
      changed = true;
    }
  }
  return changed ? next : prev;
}

export default function OrgTargetCombobox({ value, onChange, disabled = false, max }: Props) {
  const [open, setOpen] = useState(false);
  const [term, setTerm] = useState('');
  const [debouncedTerm, setDebouncedTerm] = useState('');
  /** `id → 이름` 적재분 — 후보 검색과 해소 결과가 둘 다 여기로 모인다(아래 `resolved` docblock) */
  const [knownNames, setKnownNames] = useState<Record<string, string>>({});
  const rootRef = useRef<HTMLDivElement>(null);

  // 타이핑마다 쏘지 않는다 — 선례 `DeleteUserDialog`의 후보 검색과 같은 300ms.
  useEffect(() => {
    const timer = setTimeout(() => setDebouncedTerm(term.trim()), 300);
    return () => clearTimeout(timer);
  }, [term]);

  // 외부 클릭 → 닫기(선례 `CatalogCombobox`).
  useEffect(() => {
    if (!open) return;
    const onDocClick = (e: MouseEvent) => {
      if (rootRef.current && !rootRef.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, [open]);

  /**
   * 후보 검색 — 열려 있을 때만 쏜다.
   *
   * ⚠️ 검색어가 없어도 조회한다(첫 페이지를 보여 준다) — 「무엇을 칠지 모르는」 운영자에게 빈
   * 드롭다운을 주면 조직 이름을 외우고 있어야 한다. 서버 상한이 `@Max(100)`이라 전량을 받아
   * 클라이언트에서 거를 수는 없다(그래서 **서버 검색**이다).
   */
  const search = useQuery({
    queryKey: ['admin', 'companies', 'org-target-candidates', debouncedTerm],
    queryFn: () => adminCompaniesApi.list({ q: debouncedTerm || undefined, limit: 20, sortBy: 'name', sortOrder: 'asc' }),
    enabled: open,
    staleTime: 30_000,
  });

  const searchItems = search.data?.data?.items;
  const candidates: AdminCompanyListItem[] = searchItems ?? [];

  /**
   * 후보가 오면 이름을 적재한다 — **「고를 때 해소 요청 0건」의 근거가 이 한 줄이다.**
   *
   * 운영자가 드롭다운에서 고르는 모든 id는 방금 받은 후보 목록에 있었으므로, 그 이름은 이미 손에
   * 있다. 적재해 두지 않으면 고르는 순간 그 id가 「모르는 id」가 되어 해소 요청이 나간다.
   *
   * ⚠️ dep은 `candidates`가 아니라 `searchItems`다 — `?? []` 폴백은 매 렌더 **새 배열**이라
   * effect가 매번 돈다(`mergeNames`가 루프를 막긴 하나 헛돈다). `search.data`는 react-query가
   * 참조를 유지한다.
   */
  useEffect(() => {
    if (!searchItems || searchItems.length === 0) return;
    setKnownNames((prev) => mergeNames(prev, searchItems.map((item) => [item.id, item.name] as const)));
  }, [searchItems]);

  /**
   * 고른 조직의 **이름**을 해소한다 — 수정 모드에서는 id만 들고 들어오기 때문이다.
   *
   * ⚠️ **id를 그대로 칩에 보이지 않는다.** cuid는 운영자에게 아무 뜻이 없고, 「이 공지가 어느
   * 조직을 겨누나」는 이 화면이 답해야 하는 바로 그 질문이다. 해소에 실패한 id는 **가리지 않고**
   * id 그대로 보인다(선례 `audienceLabel` — 「모르는 참조를 숨기면 운영자는 대상이 비어 있다고
   * 믿는다」). 파기·삭제된 조직이 그 모양으로 올 수 있다.
   *
   * ## ⚠️ 해소 대상은 「이름을 아직 «모르는» id」다 — 전체가 아니다 (2026-10-21 수정)
   *
   * 종전에는 쿼리 키가 **정렬된 전체 id 목록**이고 `queryFn`이 `value` 전량을 다시 쳤다. 그래서
   * 칩을 하나 더하면 키가 바뀌어 **이미 받은 것까지 전부 다시** 받았다 — 한 건씩 k개를 고르면
   * 누적 요청이 **`k(k+1)/2`**다:
   *
   * | k | 종전 누적 요청 |
   * |---|---|
   * | 10 | 55 |
   * | 20 | 210 |
   * | 50 | 1,275 |
   * | **500**(부모가 넘기는 `max` = 서버 `MAX_ORG_TARGETS`) | **≈125,000** |
   *
   * 요청 하나도 싸지 않다 — `/admin/companies/:id`는 델리게이트 2회에 **그 회사의 현장 전체**를
   * 싣는데 칩에 필요한 것은 `name` **하나**다. 그리고 한 회차의 **동시 요청이 N개**라, 수정 모달을
   * 여는 것만으로 기존 타깃 N개에 N개가 동시에 나간다.
   *
   * ⚠️ **비용만의 문제가 아니었다** — `placeholderData`도 전역 기본값도 없어(react-query v5.62) 키가
   * 바뀌는 동안 `resolved.data`가 `undefined`로 떨어지고, 종전 `nameOf`의 폴백은 **열린 드롭다운의
   * 20건만** 알았다. 그래서 **이미 고른 칩이 cuid 날것으로 깜빡였다.**
   *
   * ⇒ 적재분(`knownNames`)을 빼고 **모르는 id에만** 돈다:
   *
   * | 경로 | 누적 `detail` 요청 |
   * |---|---|
   * | 생성(빈 값에서 k개 고르기) | **0** — 후보 적재로 이름이 이미 있다 |
   * | 수정(기존 N개로 진입) | **N** — 한 라운드. 그 뒤 칩을 더해도 **0** |
   *
   * ⚠️ **「N배 빨라진다」류 숫자는 적지 않는다 — 응답 시간·SQL 왕복은 미측정이다.** 위 두 표는
   * **호출 수**이고 코드로 결정적이다.
   *
   * ⚠️ **잃는 것**: 적재한 이름은 **이 컴포넌트가 떠 있는 동안 조직 개명을 따라가지 않는다**(모달을
   * 닫고 다시 열면 받는다). 종전도 `staleTime: 5 * 60_000`이라 5분간 같은 상태였으므로, 새로 생긴
   * 성질은 그 창이 「5분」에서 「모달이 열려 있는 동안」으로 늘어난 것뿐이다.
   *
   * ⚠️ 실패한 id는 **적재하지 않는다** — 그래야 재시도가 열려 있다(전량 성공하면 그 뒤 쿼리는 꺼진다).
   * 일부만 실패하면 성공분이 빠져 키가 한 번 바뀌므로 **최대 2 라운드**이고 그 뒤 멈춘다.
   *
   * ⚠️ 한 `useQuery`에 `Promise.all`로 묶는다 — `useQueries` 선례가 레포에 **0건**이고, 무엇보다
   * `/admin/companies`에 **id 목록 필터가 없어** 한 번에 받을 입구가 아예 없다. 즉 기초 N+1은 거기서
   * 강제된 것이고, 이 수정이 없앤 것은 **제곱**이다. 입구가 생기면 이 블록을 한 번의 벌크 조회로
   * 갈아치울 수 있다.
   */
  const unresolvedIds = useMemo(() => value.filter((id) => !(id in knownNames)), [value, knownNames]);

  const resolved = useQuery({
    queryKey: ['admin', 'companies', 'org-target-names', [...unresolvedIds].sort().join(',')],
    queryFn: async () => {
      const entries = await Promise.all(
        unresolvedIds.map(async (id) => {
          try {
            const res = await adminCompaniesApi.detail(id);
            return [id, res.data?.company?.name ?? id] as const;
          } catch {
            // 파기·삭제·권한 실패 — 적재하지 않는다(위 docblock) · 칩은 id 그대로 보인다
            return null;
          }
        }),
      );
      const names: Record<string, string> = {};
      for (const entry of entries) {
        if (entry) names[entry[0]] = entry[1];
      }
      return names;
    },
    enabled: unresolvedIds.length > 0,
    staleTime: 5 * 60_000,
  });

  // 해소분도 적재한다 — 그래야 `unresolvedIds`가 비어 쿼리가 꺼진 뒤에도 칩 이름이 남는다.
  useEffect(() => {
    const names = resolved.data;
    if (!names) return;
    setKnownNames((prev) => mergeNames(prev, Object.entries(names)));
  }, [resolved.data]);

  const nameOf = (id: string): string => knownNames[id] ?? resolved.data?.[id] ?? id;

  const atMax = value.length >= max;

  const toggle = (id: string) => {
    if (value.includes(id)) {
      onChange(value.filter((v) => v !== id));
      return;
    }
    // 상한은 서버 `@ArrayMaxSize(MAX_ORG_TARGETS)`와 같은 수다 — 넘겨 보내면 400이므로 여기서 멈춘다
    if (atMax) return;
    onChange([...value, id]);
  };

  return (
    <div ref={rootRef} className="relative">
      {/* 고른 조직 — 칩 누적. 클릭하면 뺀다 */}
      {value.length > 0 && (
        <div className="flex flex-wrap gap-1 mb-2">
          {value.map((id) => (
            <button
              key={id}
              type="button"
              disabled={disabled}
              onClick={() => toggle(id)}
              title="클릭하면 제거합니다"
              className="flex items-center gap-1 px-2 py-0.5 text-xs border border-brand-200 bg-brand-50 text-brand-800 rounded-control disabled:opacity-50"
            >
              <span className="max-w-[16rem] truncate">{nameOf(id)}</span>
              <span className="text-brand-500">✕</span>
            </button>
          ))}
        </div>
      )}

      {open ? (
        <input
          type="text"
          autoFocus
          value={term}
          placeholder="조직 이름·사업자번호로 검색..."
          onChange={(e) => setTerm(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Escape') setOpen(false);
          }}
          className={FIELD_CLS}
          role="combobox"
          aria-expanded={true}
          aria-label="대상 조직 검색"
        />
      ) : (
        <button
          type="button"
          disabled={disabled}
          onClick={() => setOpen(true)}
          className={`${FIELD_CLS} text-left flex items-center justify-between gap-1 ${
            disabled ? 'bg-gray-100 text-gray-400' : ''
          }`}
          aria-haspopup="listbox"
          aria-expanded={false}
        >
          <span className="text-gray-500">
            {value.length === 0 ? '조직을 검색해 고르세요' : `${value.length}개 선택됨 — 더 고르기`}
          </span>
          <span className="text-gray-400 text-[10px]">▾</span>
        </button>
      )}

      {open && (
        <div
          role="listbox"
          className="absolute z-30 mt-0.5 w-full max-h-60 overflow-y-auto rounded-card border border-gray-300 bg-white shadow-card"
        >
          {atMax && (
            <p className="text-xs text-amber-700 bg-amber-50 px-2 py-1.5">
              최대 {max}개까지 고를 수 있습니다. 전 조직을 겨누려면 범위를 「전 조직」으로 바꾸세요.
            </p>
          )}
          {search.isLoading ? (
            <p className="text-xs text-gray-400 px-2 py-2">불러오는 중...</p>
          ) : search.error ? (
            <p className="text-xs text-red-600 px-2 py-2">조직 목록을 불러오지 못했습니다.</p>
          ) : candidates.length === 0 ? (
            <p className="text-xs text-gray-400 px-2 py-2">결과 없음</p>
          ) : (
            candidates.map((item) => {
              const picked = value.includes(item.id);
              return (
                <button
                  key={item.id}
                  type="button"
                  role="option"
                  aria-selected={picked}
                  onClick={() => toggle(item.id)}
                  disabled={!picked && atMax}
                  className={`w-full text-left text-xs px-2 py-1.5 flex items-start gap-2 hover:bg-brand-50 disabled:opacity-40 ${
                    picked ? 'bg-brand-50' : ''
                  }`}
                >
                  <span className="mt-0.5 shrink-0">{picked ? '☑' : '☐'}</span>
                  <span className="flex-1 min-w-0">
                    <span className="block break-words">{item.name}</span>
                    {/* 동명 회사 구분 — 이름만 보이면 다른 회사를 겨눈다(위 docblock) */}
                    <span className="block text-[10px] text-gray-400 break-all">{describe(item)}</span>
                  </span>
                  {/*
                    구성원 유무의 **대리 신호**다 — 가입(구독)이 없는 회사를 겨누면 아무에게도
                    보이지 않을 수 있다. ⚠️ 「구성원 0명」과 같은 말이 아니다(개인 작업공간은 구독
                    없이도 구성원이 있다) ⇒ 경고가 아니라 **중립 배지**로 둔다.
                  */}
                  {!item.hasSubscription && (
                    /*
                      ⚠️ 여기는 `&& ( … )` **표현식 슬롯**이라 중괄호로 감싼 JSX 자식 주석을 쓸 수
                      없다 — 그 중괄호가 객체 리터럴로 파싱돼 TS1005·TS17002가 줄줄이 난다(2026-10-20
                      실측 11건). 평범한 블록 주석이 맞다(선례 `ReleaseNoteAudienceModal`의
                      `{hasFailure && (` 자리). ⚠️ 중괄호 형태를 **예시로 적지도 말 것** — 그 안의
                      닫는 기호가 이 주석을 조기 종료시켜 같은 사고를 다시 만든다.

                      ⚠️ `Badge` 프리미티브를 쓰지 않는다 — 여기는 `<button>` **안**이고 그 규칙의
                      축은 「연한 tint `<span>`」이다. 채움 없는 **흰 면 + 중립 테두리**는 그 규칙이
                      「tone 개념이 없는 아웃라인 필」로 **일부러 비워 둔 갈래**다(eslint 설정 주석).
                      여기서 tone을 주면 선택 상태(brand tint)와 색이 다투어 신호가 흐려진다.
                    */
                    <span className="shrink-0 text-[9px] text-gray-500 border border-gray-200 rounded-card-sm px-1 py-0.5">
                      미가입
                    </span>
                  )}
                </button>
              );
            })
          )}
        </div>
      )}
    </div>
  );
}
